import React from 'react';

/**
 * Skeleton loading components for better UX
 */

// Basic skeleton pulse animation
export const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

// Card skeleton for dashboard cards
export const CardSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>
    <Skeleton className="h-8 w-16 mb-2" />
    <Skeleton className="h-3 w-32" />
  </div>
);

// CV card skeleton
export const CVCardSkeleton = () => (
  <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
    <div className="flex items-start justify-between mb-4">
      <div className="flex-1">
        <Skeleton className="h-5 w-48 mb-2" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-6 w-16 rounded-full" />
    </div>
    <div className="flex gap-4 mb-4">
      <div className="flex-1">
        <Skeleton className="h-3 w-20 mb-1" />
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
      <div className="flex-1">
        <Skeleton className="h-3 w-20 mb-1" />
        <Skeleton className="h-2 w-full rounded-full" />
      </div>
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-9 flex-1 rounded-lg" />
      <Skeleton className="h-9 flex-1 rounded-lg" />
      <Skeleton className="h-9 w-9 rounded-lg" />
    </div>
  </div>
);

// Table row skeleton
export const TableRowSkeleton = ({ columns = 4 }) => (
  <tr className="border-b border-slate-100">
    {Array(columns).fill(0).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <Skeleton className="h-4 w-full max-w-[120px]" />
      </td>
    ))}
  </tr>
);

// List item skeleton
export const ListItemSkeleton = () => (
  <div className="flex items-center gap-4 p-4 border-b border-slate-100">
    <Skeleton className="h-10 w-10 rounded-full" />
    <div className="flex-1">
      <Skeleton className="h-4 w-32 mb-2" />
      <Skeleton className="h-3 w-48" />
    </div>
    <Skeleton className="h-8 w-20 rounded-lg" />
  </div>
);

// Form field skeleton
export const FormFieldSkeleton = () => (
  <div className="mb-4">
    <Skeleton className="h-4 w-24 mb-2" />
    <Skeleton className="h-10 w-full rounded-lg" />
  </div>
);

// Full page loading skeleton
export const PageSkeleton = ({ title = true, cards = 3 }) => (
  <div className="p-6">
    {title && <Skeleton className="h-8 w-48 mb-6" />}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array(cards).fill(0).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  </div>
);

// Dashboard stats skeleton
export const DashboardStatsSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
    {Array(4).fill(0).map((_, i) => (
      <CardSkeleton key={i} />
    ))}
  </div>
);

// Text block skeleton
export const TextBlockSkeleton = ({ lines = 3 }) => (
  <div className="space-y-2">
    {Array(lines).fill(0).map((_, i) => (
      <Skeleton 
        key={i} 
        className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`} 
      />
    ))}
  </div>
);

export default Skeleton;
